import React, { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button, Input } from '@/components/common';
import { Region, RoutePricingData } from '@/types';
import { toast } from 'react-hot-toast';

interface RouteFormProps {
  regions: Region[];
  initialData?: Partial<RoutePricingData> & {
    fromRegionName?: string;
    toRegionName?: string;
  };
  onSubmit: (data: RoutePricingData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * RouteForm Component
 * Form for adding or editing route pricing
 */
export const RouteForm: React.FC<RouteFormProps> = ({
  regions,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<RoutePricingData>({
    fromRegionId: initialData?.fromRegionId || 0,
    toRegionId: initialData?.toRegionId || 0,
    price: initialData?.price || 0,
    isAvailable: initialData?.isAvailable ?? true,
    estimatedTimeMinutes: initialData?.estimatedTimeMinutes,
    distanceKm: initialData?.distanceKm,
    notes: initialData?.notes || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fromGovernorateFilter, setFromGovernorateFilter] = useState('');
  const [toGovernorateFilter, setToGovernorateFilter] = useState('');

  // Get unique governorates
  const governorates = Array.from(
    new Set(regions.map(r => r.governorateAr))
  ).sort();

  // Filter regions by governorate
  const filteredFromRegions = fromGovernorateFilter
    ? regions.filter(r => r.governorateAr === fromGovernorateFilter)
    : regions;

  const filteredToRegions = toGovernorateFilter
    ? regions.filter(r => r.governorateAr === toGovernorateFilter)
    : regions;

  const isEditMode = !!initialData?.fromRegionId;

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fromRegionId || formData.fromRegionId === 0) {
      newErrors.fromRegionId = 'يرجى اختيار منطقة البداية';
    }

    if (!formData.toRegionId || formData.toRegionId === 0) {
      newErrors.toRegionId = 'يرجى اختيار منطقة الوجهة';
    }

    if (formData.fromRegionId === formData.toRegionId && formData.fromRegionId !== 0) {
      newErrors.toRegionId = 'يجب أن تكون منطقة الوجهة مختلفة عن منطقة البداية';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'يرجى إدخال سعر صحيح أكبر من صفر';
    }

    if (formData.estimatedTimeMinutes && formData.estimatedTimeMinutes < 0) {
      newErrors.estimatedTimeMinutes = 'الوقت المتوقع يجب أن يكون موجباً';
    }

    if (formData.distanceKm && formData.distanceKm < 0) {
      newErrors.distanceKm = 'المسافة يجب أن تكون موجبة';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('يرجى تصحيح الأخطاء في النموذج');
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleFieldChange = (field: keyof RoutePricingData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-dark-lighter border border-gray-700 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-primary">
            {isEditMode ? 'تعديل مسار' : 'إضافة مسار جديد'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* From Region */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                منطقة البداية *
              </label>
              
              {/* Governorate Filter for From */}
              <select
                value={fromGovernorateFilter}
                onChange={(e) => setFromGovernorateFilter(e.target.value)}
                className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors mb-2"
                disabled={isEditMode || isSubmitting}
              >
                <option value="">جميع المحافظات</option>
                {governorates.map((gov) => (
                  <option key={gov} value={gov}>
                    {gov}
                  </option>
                ))}
              </select>

              <select
                value={formData.fromRegionId}
                onChange={(e) => handleFieldChange('fromRegionId', parseInt(e.target.value))}
                className={`w-full bg-dark border rounded-lg px-4 py-2.5 text-gray-200 focus:outline-none focus:ring-1 transition-colors ${
                  errors.fromRegionId
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-700 focus:border-primary focus:ring-primary'
                }`}
                disabled={isEditMode || isSubmitting}
              >
                <option value="0">اختر منطقة البداية</option>
                {filteredFromRegions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.nameAr} - {region.governorateAr}
                  </option>
                ))}
              </select>
              {errors.fromRegionId && (
                <p className="mt-1 text-sm text-red-500">{errors.fromRegionId}</p>
              )}
            </div>

            {/* To Region */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                منطقة الوجهة *
              </label>
              
              {/* Governorate Filter for To */}
              <select
                value={toGovernorateFilter}
                onChange={(e) => setToGovernorateFilter(e.target.value)}
                className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors mb-2"
                disabled={isEditMode || isSubmitting}
              >
                <option value="">جميع المحافظات</option>
                {governorates.map((gov) => (
                  <option key={gov} value={gov}>
                    {gov}
                  </option>
                ))}
              </select>

              <select
                value={formData.toRegionId}
                onChange={(e) => handleFieldChange('toRegionId', parseInt(e.target.value))}
                className={`w-full bg-dark border rounded-lg px-4 py-2.5 text-gray-200 focus:outline-none focus:ring-1 transition-colors ${
                  errors.toRegionId
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-700 focus:border-primary focus:ring-primary'
                }`}
                disabled={isEditMode || isSubmitting}
              >
                <option value="0">اختر منطقة الوجهة</option>
                {filteredToRegions
                  .filter(r => r.id !== formData.fromRegionId)
                  .map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nameAr} - {region.governorateAr}
                    </option>
                  ))}
              </select>
              {errors.toRegionId && (
                <p className="mt-1 text-sm text-red-500">{errors.toRegionId}</p>
              )}
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              السعر (د.ك) *
            </label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={formData.price}
              onChange={(e) => handleFieldChange('price', parseFloat(e.target.value) || 0)}
              placeholder="0.000"
              disabled={isSubmitting}
              className={errors.price ? 'border-red-500' : ''}
            />
            {errors.price && (
              <p className="mt-1 text-sm text-red-500">{errors.price}</p>
            )}
          </div>

          {/* Optional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الوقت المتوقع (دقيقة)
              </label>
              <Input
                type="number"
                min="0"
                value={formData.estimatedTimeMinutes || ''}
                onChange={(e) =>
                  handleFieldChange(
                    'estimatedTimeMinutes',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                placeholder="30"
                disabled={isSubmitting}
                className={errors.estimatedTimeMinutes ? 'border-red-500' : ''}
              />
              {errors.estimatedTimeMinutes && (
                <p className="mt-1 text-sm text-red-500">{errors.estimatedTimeMinutes}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                المسافة (كم)
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={formData.distanceKm || ''}
                onChange={(e) =>
                  handleFieldChange(
                    'distanceKm',
                    e.target.value ? parseFloat(e.target.value) : undefined
                  )
                }
                placeholder="15.5"
                disabled={isSubmitting}
                className={errors.distanceKm ? 'border-red-500' : ''}
              />
              {errors.distanceKm && (
                <p className="mt-1 text-sm text-red-500">{errors.distanceKm}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              ملاحظات
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              rows={3}
              className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors resize-none"
              placeholder="أدخل أي ملاحظات إضافية..."
              disabled={isSubmitting}
            />
          </div>

          {/* Availability Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isAvailable"
              checked={formData.isAvailable}
              onChange={(e) => handleFieldChange('isAvailable', e.target.checked)}
              className="rounded border-gray-700 text-primary focus:ring-primary"
              disabled={isSubmitting}
            />
            <label htmlFor="isAvailable" className="text-sm text-gray-300">
              المسار متاح
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditMode ? 'تحديث المسار' : 'إضافة المسار'}
                </>
              )}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              variant="secondary"
              disabled={isSubmitting}
              className="flex-1"
            >
              إلغاء
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RouteForm;
